import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';
import InjectJobModal from '../components/InjectJobModal';
import EditJobModal from '../components/EditJobModal';
import SideDrawer from '../components/SideDrawer';
import SlicerQuoteDetail from './SlicerQuoteDetail';
import OrderDetail from './OrderDetail';
import SlicerJobDetail from './SlicerJobDetail';
import {
  ArrowPathIcon,
  PlusIcon,
  PlayIcon,
  CheckIcon,
  XMarkIcon,
  PauseIcon,
  MoonIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';

const STATUS_COLORS = {
  PENDING_SLICING: 'bg-yellow-100 text-yellow-800',
  READY_TO_PRINT: 'bg-blue-100 text-blue-800',
  PRINTING: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  ON_HOLD: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-slate-100 text-slate-800',
};

const formatStatus = (s) => s.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

// Generate a consistent color from a string (quote ID) for visual grouping
const QUOTE_COLORS = [
  'border-l-blue-400', 'border-l-emerald-400', 'border-l-violet-400',
  'border-l-amber-400', 'border-l-rose-400', 'border-l-cyan-400',
  'border-l-fuchsia-400', 'border-l-lime-400', 'border-l-orange-400',
  'border-l-teal-400', 'border-l-indigo-400', 'border-l-pink-400',
];
const getQuoteBorderColor = (quoteId) => {
  if (!quoteId) return '';
  let hash = 0;
  for (let i = 0; i < quoteId.length; i++) hash = (hash * 31 + quoteId.charCodeAt(i)) | 0;
  return QUOTE_COLORS[Math.abs(hash) % QUOTE_COLORS.length];
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const TABS = { DASHBOARD: 'dashboard', NIGHT_SHIFT: 'nightShift' };

export default function PrintQueue() {
  const [activeTab, setActiveTab] = useState(TABS.DASHBOARD);
  const [queue, setQueue] = useState([]);
  const [ongoing, setOngoing] = useState([]);
  const [nightShift, setNightShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInjectModal, setShowInjectModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [drawer, setDrawer] = useState({ open: false, type: null, id: null, title: '' });
  const [editQuoteId, setEditQuoteId] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/print-queue/dashboard', { params: { limit: 100 } });
      const data = response.data.data || {};
      setQueue(data.queue || []);
      setOngoing(data.ongoing || []);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNightShift = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/print-queue/night-shift');
      setNightShift(response.data.data || { visible: false });
    } catch (err) {
      console.error('Failed to fetch night shift:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === TABS.DASHBOARD) fetchDashboard();
    else fetchNightShift();
  }, [activeTab, fetchDashboard, fetchNightShift]);

  const performAction = async (actionFn) => {
    try {
      await actionFn();
      if (activeTab === TABS.DASHBOARD) fetchDashboard();
      else fetchNightShift();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Action failed');
    }
  };

  const handlePick = async (jobId) => {
    setActionLoading(jobId);
    await performAction(() => api.patch(`/auth/print-queue/jobs/${jobId}/pick`));
    setActionLoading(null);
  };

  const handleCompleteUnit = async (jobId) => {
    setActionLoading(jobId);
    await performAction(() => api.patch(`/auth/print-queue/jobs/${jobId}/complete-unit`));
    setActionLoading(null);
  };

  const handleFailUnit = async (jobId) => {
    setActionLoading(jobId);
    await performAction(() => api.patch(`/auth/print-queue/jobs/${jobId}/fail-unit`));
    setActionLoading(null);
  };

  const handleHold = (job) => {
    setConfirmAction({
      title: 'Put Job On Hold',
      message: `Put job for plate ${job.plateIndex} on hold?`,
      onConfirm: async () => {
        await performAction(() => api.patch(`/auth/print-queue/jobs/${job._id}/status`, { status: 'ON_HOLD' }));
        setConfirmAction(null);
      }
    });
  };

  const handleResume = async (jobId) => {
    setActionLoading(jobId);
    await performAction(() => api.patch(`/auth/print-queue/jobs/${jobId}/status`, { status: 'READY_TO_PRINT' }));
    setActionLoading(null);
  };

  const handleCancel = (job) => {
    setConfirmAction({
      title: 'Cancel Job',
      message: `Cancel job for plate ${job.plateIndex}? This cannot be undone.`,
      onConfirm: async () => {
        await performAction(() => api.delete(`/auth/print-queue/jobs/${job._id}`));
        setConfirmAction(null);
      }
    });
  };

  const handleDownload = async (job) => {
    try {
      const response = await api.get(`/auth/print-queue/jobs/${job._id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plate-${job.plateIndex}.3mf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'Download failed');
    }
  };

  const URGENCY_STYLE = {
    OVERDUE: 'text-red-600 font-bold',
    Urgent: 'text-orange-600 font-semibold',
    Normal: 'text-yellow-600',
    Ahead: 'text-green-600',
    'On Hold': 'text-gray-400',
  };

  const getUrgencyLabelFromScore = (score) => {
    if (score === undefined || score === null) return '';
    if (score === 0) return 'On Hold';
    if (score >= 9999999) return 'OVERDUE';
    if (score >= 1) return 'Urgent';
    if (score >= 0.5) return 'Normal';
    return 'Ahead';
  };

  const resolveUrgency = (job) => {
    const label = job.urgencyLabel || getUrgencyLabelFromScore(job.urgencyScore);
    const color = URGENCY_STYLE[label] || 'text-gray-400';
    return { label, color };
  };

  const renderQueueCard = (job) => {
    const quoteName = job.slicerQuote?.contact?.name || job.slicerQuote?.contact?.email || 'Unknown';
    const machineName = job.targetMachine?.name || job.targetMachine?.uid || '-';
    const remaining = (job.quantityTotal || 0) - (job.quantityCompleted || 0) - (job.quantityInProgress || 0);
    const isLoading = actionLoading === job._id;

    const quoteBorder = getQuoteBorderColor(job.slicerQuote?._id);

    return (
      <div key={job._id} className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${quoteBorder ? `border-l-4 ${quoteBorder}` : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <button
              onClick={() => setDrawer({ open: true, type: 'quote', id: job.slicerQuote?._id, title: `Quote — ${quoteName}` })}
              className="font-semibold text-gray-900 hover:text-blue-700 truncate block text-left transition-colors"
            >
              {quoteName}
            </button>
            {job.slicerJob?.file?.name && (
              <p className="text-xs text-gray-500 truncate mt-0.5" title={job.slicerJob.file.name}>
                {job.slicerJob.file.name}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <span className="text-xs text-gray-500">Plate {job.plateIndex}</span>
              <span className="text-xs text-gray-300">&middot;</span>
              <span className="text-xs text-gray-500">{machineName}</span>
              {job.slicerJob && (
                <>
                  <span className="text-xs text-gray-300">&middot;</span>
                  <button
                    onClick={() => setDrawer({ open: true, type: 'slicerJob', id: job.slicerJob._id, title: `Slicer Job` })}
                    className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                  >
                    Job: {job.slicerJob.status}
                  </button>
                </>
              )}
              {job.slicerQuote?._id && (
                <>
                  <span className="text-xs text-gray-300">&middot;</span>
                  <button
                    onClick={() => setDrawer({ open: true, type: 'quote', id: job.slicerQuote._id, title: `Quote — ${quoteName}` })}
                    className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    Quote
                  </button>
                </>
              )}
              {job.linkedOrderNumber && (
                <>
                  <span className="text-xs text-gray-300">&middot;</span>
                  <button
                    onClick={() => setDrawer({ open: true, type: 'order', id: job.linkedOrderId, title: `Order ${job.linkedOrderNumber}` })}
                    className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    {job.linkedOrderNumber}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 ml-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-600'}`}>
              {formatStatus(job.status)}
            </span>
            {(job.urgencyScore !== undefined || job.urgencyLabel) && (() => {
              const { label, color } = resolveUrgency(job);
              return label ? <span className={`text-xs ${color}`}>{label}</span> : null;
            })()}
          </div>
        </div>

        {/* Machine Busy Indicator */}
        {job.machineBusy && (
          <div className="flex items-center gap-1.5 mb-3 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
            <ClockIcon className="h-3.5 w-3.5 shrink-0" />
            <span>Waiting for machine — concurrency cap reached</span>
          </div>
        )}

        {/* Quantities */}
        <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
          <div>
            <div className="text-gray-500">Total</div>
            <div className="font-semibold">{job.quantityTotal}</div>
          </div>
          <div>
            <div className="text-gray-500">Done</div>
            <div className="font-semibold text-green-600">{job.quantityCompleted}</div>
          </div>
          <div>
            <div className="text-gray-500">Remain</div>
            <div className="font-semibold text-orange-600">{remaining}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-3">
          <div
            className="bg-green-500 h-1.5 rounded-full transition-all"
            style={{ width: `${job.quantityTotal ? (job.quantityCompleted / job.quantityTotal) * 100 : 0}%` }}
          />
        </div>

        {/* Meta */}
        <div className="text-xs text-gray-500 space-y-1 mb-3">
          <div className="flex justify-between">
            <span>Print Time</span>
            <span>{formatDuration(job.printTimeSeconds)}</span>
          </div>
          <div className="flex justify-between">
            <span>Deadline</span>
            <span className={resolveUrgency(job).label === 'OVERDUE' ? 'text-red-600 font-semibold' : ''}>{formatDate(job.deadline)}</span>
          </div>
          <div className="flex justify-between">
            <span>Mode</span>
            <span>{job.sequenceMode}</span>
          </div>
        </div>

        {job.instructions && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700 line-clamp-2">
            {job.instructions}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
          {job.status === 'READY_TO_PRINT' && remaining > 0 && (
            <button
              onClick={() => handlePick(job._id)}
              disabled={isLoading || job.machineBusy}
              title={job.machineBusy ? 'Machine is busy — concurrency cap reached' : 'Pick next unit'}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded disabled:opacity-50 ${job.machineBusy
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
            >
              <PlayIcon className="h-3.5 w-3.5" /> Pick
            </button>
          )}
          {job.status === 'ON_HOLD' && (
            <button
              onClick={() => handleResume(job._id)}
              disabled={isLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
            >
              <PlayIcon className="h-3.5 w-3.5" /> Resume
            </button>
          )}
          {job.status === 'FAILED' && (
            <button
              onClick={() => handleResume(job._id)}
              disabled={isLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" /> Retry
            </button>
          )}
          {['READY_TO_PRINT', 'PENDING_SLICING'].includes(job.status) && job.slicerQuote?._id && (
            <button
              onClick={() => setEditQuoteId(job.slicerQuote._id)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          {job.s3Key3MF && (
            <button
              onClick={() => handleDownload(job)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" /> 3MF
            </button>
          )}
          {!['COMPLETED', 'CANCELLED', 'ON_HOLD'].includes(job.status) && (
            <button
              onClick={() => handleHold(job)}
              disabled={isLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              <PauseIcon className="h-3.5 w-3.5" /> Hold
            </button>
          )}
          {!['COMPLETED', 'CANCELLED'].includes(job.status) && (
            <button
              onClick={() => handleCancel(job)}
              disabled={isLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50"
            >
              <XMarkIcon className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderOngoingRow = (job) => {
    const quoteName = job.slicerQuote?.contactName || job.slicerQuote?.contactEmail || 'Unknown';
    const machineName = job.targetMachine?.name || job.targetMachine?.uid || '-';
    const pct = job.quantityTotal ? Math.round((job.quantityCompleted / job.quantityTotal) * 100) : 0;
    const isLoading = actionLoading === job._id;
    const quoteBorder = getQuoteBorderColor(job.slicerQuote?._id);

    return (
      <div key={job._id} className={`bg-white border border-indigo-200 rounded-lg p-3 ${quoteBorder ? `border-l-4 ${quoteBorder}` : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0 flex-1">
            <button
              onClick={() => setDrawer({ open: true, type: 'quote', id: job.slicerQuote?._id, title: `Quote — ${quoteName}` })}
              className="font-medium text-sm text-gray-900 hover:text-blue-700 truncate block text-left transition-colors"
            >
              {quoteName}
            </button>
            {job.slicerQuote?.file?.name && (
              <p className="text-xs text-gray-500 truncate mt-0.5" title={job.slicerQuote.file.name}>
                {job.slicerQuote.file.name}
              </p>
            )}
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-gray-500">Plate {job.plateIndex}</span>
              <span className="text-xs text-gray-300">&middot;</span>
              <span className="text-xs text-gray-500">{machineName}</span>
              {job.slicerJob && (
                <>
                  <span className="text-xs text-gray-300">&middot;</span>
                  <button
                    onClick={() => setDrawer({ open: true, type: 'slicerJob', id: job.slicerJob._id, title: 'Slicer Job' })}
                    className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                  >
                    Job
                  </button>
                </>
              )}
              {job.linkedOrderNumber && (
                <>
                  <span className="text-xs text-gray-300">&middot;</span>
                  <button
                    onClick={() => setDrawer({ open: true, type: 'order', id: job.linkedOrderId, title: `Order ${job.linkedOrderNumber}` })}
                    className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    {job.linkedOrderNumber}
                  </button>
                </>
              )}
            </div>
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 ml-2 shrink-0">
            Printing
          </span>
        </div>

        {/* Compact progress */}
        <div className="flex items-center gap-3 text-xs mb-2">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <span className="text-gray-600 shrink-0">{job.quantityCompleted}/{job.quantityTotal}</span>
          <span className="text-indigo-600 font-medium shrink-0">{job.quantityInProgress} active</span>
        </div>

        {/* Compact meta */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
          <span>{formatDuration(job.printTimeSeconds)}/unit</span>
          <span>&middot;</span>
          <span>{formatDate(job.deadline)}</span>
          {job.sequenceMode && (
            <>
              <span>&middot;</span>
              <span>{job.sequenceMode}</span>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-1.5">
          {job.quantityInProgress > 0 && (
            <>
              <button
                onClick={() => handleCompleteUnit(job._id)}
                disabled={isLoading}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                <CheckIcon className="h-3.5 w-3.5" /> Done
              </button>
              <button
                onClick={() => handleFailUnit(job._id)}
                disabled={isLoading}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50"
              >
                <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Fail
              </button>
            </>
          )}
          {job.s3Key3MF && (
            <button
              onClick={() => handleDownload(job)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" /> 3MF
            </button>
          )}
          <button
            onClick={() => handleHold(job)}
            disabled={isLoading}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <PauseIcon className="h-3.5 w-3.5" /> Hold
          </button>
          <button
            onClick={() => handleCancel(job)}
            disabled={isLoading}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50"
          >
            <XMarkIcon className="h-3.5 w-3.5" /> Cancel
          </button>
        </div>
      </div>
    );
  };

  const hasNoJobs = queue.length === 0 && ongoing.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Print Queue"
        description="Farm queue dashboard sorted by urgency"
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => activeTab === TABS.DASHBOARD ? fetchDashboard() : fetchNightShift()}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" /> Refresh
            </button>
            <button
              onClick={() => setShowInjectModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-2" /> Inject Quote
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab(TABS.DASHBOARD)}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === TABS.DASHBOARD
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <ClockIcon className="h-4 w-4 inline mr-1 -mt-0.5" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab(TABS.NIGHT_SHIFT)}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === TABS.NIGHT_SHIFT
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <MoonIcon className="h-4 w-4 inline mr-1 -mt-0.5" />
            Night Shift
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : activeTab === TABS.DASHBOARD ? (
        hasNoJobs ? (
          <div className="text-center py-12 text-gray-400">
            <ClockIcon className="h-12 w-12 mx-auto mb-3" />
            <p className="text-sm">No jobs in queue</p>
            <p className="text-xs mt-1">Inject a paid slicer quote to get started</p>
          </div>
        ) : (
          /* Two-column layout: Queue (left) + Ongoing (right) */
          <div className="flex gap-6">
            {/* Queue - left side */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Queue</h2>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{queue.length}</span>
              </div>
              {queue.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-lg">
                  No jobs waiting in queue
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {queue.map(renderQueueCard)}
                </div>
              )}
            </div>

            {/* Ongoing - right side, stacked list */}
            <div className="w-80 shrink-0">
              <div className="sticky top-6">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">In Progress</h2>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{ongoing.length}</span>
                </div>
                {ongoing.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-lg">
                    No active prints
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ongoing.map(renderOngoingRow)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      ) : (
        /* Night Shift */
        !nightShift?.visible ? (
          <div className="text-center py-12 text-gray-400">
            <MoonIcon className="h-12 w-12 mx-auto mb-3" />
            <p className="text-sm">Night shift suggestions are not visible right now</p>
            <p className="text-xs mt-1">They appear 1 hour before business day ends</p>
          </div>
        ) : (
          <div className="space-y-4">
            {nightShift.window && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-800">
                <div className="font-medium mb-1">Night Shift Window</div>
                <div>Off-hours: {formatDuration(nightShift.window.offHoursSeconds)}</div>
                <div>Max print time: {formatDuration(nightShift.window.maxPrintTimeSeconds)}</div>
              </div>
            )}
            {nightShift.suggestions?.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No eligible jobs for tonight</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {nightShift.suggestions?.map(renderQueueCard)}
              </div>
            )}
          </div>
        )
      )}

      {/* Inject Modal */}
      {showInjectModal && (
        <InjectJobModal
          onClose={() => setShowInjectModal(false)}
          onSuccess={() => {
            setShowInjectModal(false);
            fetchDashboard();
          }}
        />
      )}

      {/* Edit Job Modal */}
      {editQuoteId && (
        <EditJobModal
          quoteId={editQuoteId}
          onClose={() => setEditQuoteId(null)}
          onSuccess={() => {
            setEditQuoteId(null);
            fetchDashboard();
          }}
        />
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <ConfirmationModal
          title={confirmAction.title}
          message={confirmAction.message}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Side Drawer */}
      <SideDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, type: null, id: null, title: '' })}
        title={drawer.title}
      >
        {drawer.type === 'quote' && drawer.id && (
          <SlicerQuoteDetail quoteId={drawer.id} embedded />
        )}
        {drawer.type === 'slicerJob' && drawer.id && (
          <SlicerJobDetail jobId={drawer.id} embedded />
        )}
        {drawer.type === 'order' && drawer.id && (
          <OrderDetail orderId={drawer.id} embedded />
        )}
      </SideDrawer>
    </div>
  );
}
